import { DealershipFilter, SiteNetwork, SiteSearchFiltersInput } from '@domain/graphql.types';

export type Dealership = 'all' | 'exclude' | 'only';

/** Filters of a single address search. `networkIds: []` means no network restriction. */
export type SearchFilters = {
  networkIds: string[];
  minimumPortsPerStation: number | null;
  minimumPowerKwPerPort: number | null;
  /** YYYY-MM-DD, inclusive */
  openedFrom: string | null;
  /** YYYY-MM-DD, inclusive */
  openedTo: string | null;
  dealership: Dealership;
};

export const defaultSearchFilters: SearchFilters = {
  networkIds: [],
  minimumPortsPerStation: null,
  minimumPowerKwPerPort: null,
  openedFrom: null,
  openedTo: null,
  dealership: 'all',
};

export type FilterCategory = 'network' | 'minimumPorts' | 'minimumPower' | 'openingDate' | 'dealership';

/** Every category, in display order. */
export const filterCategories: readonly FilterCategory[] = ['network', 'minimumPorts', 'minimumPower', 'openingDate', 'dealership'];

/** Restrictive categories, in display order. Several networks or two date bounds count as one category. */
export const getActiveCategories = (filters: SearchFilters): FilterCategory[] => {
  const categories: FilterCategory[] = [];

  if (filters.networkIds.length) categories.push('network');
  if (filters.minimumPortsPerStation !== null) categories.push('minimumPorts');
  if (filters.minimumPowerKwPerPort !== null) categories.push('minimumPower');
  if (filters.openedFrom || filters.openedTo) categories.push('openingDate');
  if (filters.dealership !== 'all') categories.push('dealership');

  return categories;
};

/** Returns `target` with the given category taken from `source` (both date bounds for the date category). */
export const withCategory = (target: SearchFilters, source: SearchFilters, category: FilterCategory): SearchFilters => {
  switch (category) {
    case 'network':
      return { ...target, networkIds: [...source.networkIds] };
    case 'minimumPorts':
      return { ...target, minimumPortsPerStation: source.minimumPortsPerStation };
    case 'minimumPower':
      return { ...target, minimumPowerKwPerPort: source.minimumPowerKwPerPort };
    case 'openingDate':
      return { ...target, openedFrom: source.openedFrom, openedTo: source.openedTo };
    case 'dealership':
      return { ...target, dealership: source.dealership };
  }
};

const isSameIdSet = (a: readonly string[], b: readonly string[]) => {
  const setA = new Set(a);
  const setB = new Set(b);

  return setA.size === setB.size && [...setA].every((id) => setB.has(id));
};

/** Networks are compared as a set of ids, regardless of their order. */
export const areSearchFiltersEqual = (a: SearchFilters, b: SearchFilters) =>
  isSameIdSet(a.networkIds, b.networkIds) &&
  a.minimumPortsPerStation === b.minimumPortsPerStation &&
  a.minimumPowerKwPerPort === b.minimumPowerKwPerPort &&
  a.openedFrom === b.openedFrom &&
  a.openedTo === b.openedTo &&
  a.dealership === b.dealership;

const dealershipInput: Record<Dealership, DealershipFilter> = {
  all: DealershipFilter.All,
  exclude: DealershipFilter.Exclude,
  only: DealershipFilter.Only,
};

/** GraphQL input of the applied filters; undefined when nothing restricts the search. */
export const toSearchFiltersInput = (filters: SearchFilters): SiteSearchFiltersInput | undefined => {
  if (!getActiveCategories(filters).length) {
    return undefined;
  }

  return {
    networkIds: filters.networkIds,
    minimumPortsPerStation: filters.minimumPortsPerStation,
    minimumPowerKwPerPort: filters.minimumPowerKwPerPort,
    openedFrom: filters.openedFrom,
    openedTo: filters.openedTo,
    dealership: dealershipInput[filters.dealership],
  };
};

/** What the user is editing: numbers stay as typed so invalid values are reported, never corrected. */
export type FiltersDraft = {
  networkIds: string[];
  minimumPortsPerStation: string;
  minimumPowerKwPerPort: string;
  openedFrom: string;
  openedTo: string;
  dealership: Dealership;
};

export type FiltersDraftErrors = Partial<Record<'networkIds' | 'minimumPortsPerStation' | 'minimumPowerKwPerPort' | 'openedTo', string>>;

/**
 * The API refuses more network ids than this (the same limit in the WebApp API and data-api).
 * The catalogue is far smaller, so only a selection of almost every network can reach it.
 */
export const MAX_FILTER_NETWORK_IDS = 500;

export const toFiltersDraft = (filters: SearchFilters): FiltersDraft => ({
  networkIds: [...filters.networkIds],
  minimumPortsPerStation: filters.minimumPortsPerStation?.toString() ?? '',
  minimumPowerKwPerPort: filters.minimumPowerKwPerPort?.toString() ?? '',
  openedFrom: filters.openedFrom ?? '',
  openedTo: filters.openedTo ?? '',
  dealership: filters.dealership,
});

const WHOLE_NUMBER = /^\d+$/;
const DECIMAL_NUMBER = /^(\d+\.?\d*|\.\d+)$/;

/**
 * Validates the draft. The returned error values are i18n keys.
 */
export const parseFiltersDraft = (draft: FiltersDraft): { filters: SearchFilters; errors: FiltersDraftErrors } => {
  const errors: FiltersDraftErrors = {};

  if (new Set(draft.networkIds).size > MAX_FILTER_NETWORK_IDS) {
    errors.networkIds = 'siteLookup.filters.errors.networks';
  }

  const ports = draft.minimumPortsPerStation.trim();
  let minimumPortsPerStation: number | null = null;
  if (ports) {
    minimumPortsPerStation = WHOLE_NUMBER.test(ports) ? Number(ports) : Number.NaN;
    if (!(minimumPortsPerStation >= 1)) {
      errors.minimumPortsPerStation = 'siteLookup.filters.errors.minimumPorts';
    }
  }

  const power = draft.minimumPowerKwPerPort.trim();
  let minimumPowerKwPerPort: number | null = null;
  if (power) {
    minimumPowerKwPerPort = DECIMAL_NUMBER.test(power) ? Number(power) : Number.NaN;
    if (!(minimumPowerKwPerPort > 0)) {
      errors.minimumPowerKwPerPort = 'siteLookup.filters.errors.minimumPower';
    }
  }

  const openedFrom = draft.openedFrom || null;
  const openedTo = draft.openedTo || null;
  // YYYY-MM-DD strings compare chronologically.
  if (openedFrom && openedTo && openedTo < openedFrom) {
    errors.openedTo = 'siteLookup.filters.errors.openingDate';
  }

  return {
    filters: { networkIds: [...draft.networkIds], minimumPortsPerStation, minimumPowerKwPerPort, openedFrom, openedTo, dealership: draft.dealership },
    errors,
  };
};

/** Case- and accent-insensitive form used to search network names. */
export const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase()
    .trim();

export const compareNetworkNames = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/** Names of the selected networks, alphabetically; `undefined` for an id missing from the catalogue. */
export const getSelectedNetworkNames = (networkIds: readonly string[], catalogue: readonly SiteNetwork[] | undefined) => {
  const namesById = new Map((catalogue ?? []).map(({ id, name }) => [id, name]));

  return networkIds.map((id) => namesById.get(id)).sort((a, b) => (a === undefined ? 1 : b === undefined ? -1 : compareNetworkNames(a, b)));
};
